import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    alpha,
    Box,
} from '@mui/material'
import { useAppSelector } from '../../../../redux/hook'
import { findEntityById } from '../../../../utils/EntityUtils'
import { ReactElement, useMemo } from 'react'
import theme from '../../../../theme/theme'
import { useTranslation } from 'react-i18next'
import { UserPermissions } from '../../../../constants/constants'

const AttributeTable = (): ReactElement => {
    const { t } = useTranslation()
    const fmSlice = useAppSelector((state) => state.facilityMonitoring)
    const navList = useAppSelector((state) => state.nav.navList)
    const permissions = useAppSelector((state) => state.org.permissions)
    const isSuperAdmin = permissions?.includes(UserPermissions.ORG_ADMIN)
    const { selectedBuilding, buildingEntityTemplateId } = fmSlice

    const buildingEntityTemplateName = findEntityById(
        navList,
        buildingEntityTemplateId,
    ).name
    const buildingName = buildingEntityTemplateName
        ? t(buildingEntityTemplateName)
        : t('Building')

    const attributesToDisplay = useMemo(() => {
        if (!selectedBuilding?.entityInstanceId) return []
        const attrs = [
            {
                label: t('id', 'ID'),
                value: selectedBuilding.id || '',
                isUrl: false,
                adminOnly: true,
            },
            {
                label: t('buildingName', 'Building Name'),
                value:
                    selectedBuilding.buildingName ||
                    selectedBuilding.name ||
                    selectedBuilding.uid ||
                    '',
                isUrl: false,
                adminOnly: false,
            },
            {
                label: t('licenseDuration', 'License Duration'),
                value: selectedBuilding.licenseDuration || '',
                isUrl: false,
                adminOnly: true,
            },
            {
                label: t('layout', 'Layout'),
                value: selectedBuilding.layout || '',
                isUrl: true,
                adminOnly: false,
            },
        ]

        return attrs.filter((attr) => !attr.adminOnly || isSuperAdmin)
    }, [selectedBuilding, t, isSuperAdmin])

    const onClickURL = (url: string) => {
        window.open(url)
    }

    return (
        <>
            <Typography
                component="h2"
                variant="h5"
                sx={{ fontSize: '20px', p: 2, height: '64.7px' }}
            >
                {t('detailsTitle', { buildingName })}
            </Typography>
            {!selectedBuilding?.entityInstanceId ? (
                <Typography
                    component={'span'}
                    variant="body1"
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        fontStyle: 'italic',
                        marginTop: '50px',
                    }}
                >
                    {t('selectToViewAttributes', { buildingName })}
                </Typography>
            ) : (
                <Box sx={{ padding: '8px 16px 16px' }}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead
                                sx={{
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.21,
                                    ),
                                }}
                            >
                                <TableRow>
                                    <TableCell sx={{ border: 'none' }}>
                                        {t('attribute')}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none' }}>
                                        {t('value')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {attributesToDisplay.map((attribute, index) => {
                                    return (
                                        <TableRow key={index}>
                                            <TableCell>
                                                {attribute.label}
                                            </TableCell>
                                            <TableCell>
                                                {attribute.isUrl &&
                                                attribute.value ? (
                                                    <Typography
                                                        id={`fm-building-attribute-link-${index}`}
                                                        onClick={() =>
                                                            onClickURL(
                                                                attribute.value,
                                                            )
                                                        }
                                                        sx={{
                                                            fontSize: '14px',
                                                            color: '#6d6dff',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        {t('clickHere')}
                                                    </Typography>
                                                ) : (
                                                    attribute.value
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </>
    )
}

export default AttributeTable
